import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPlayerMapRoles, getPlayerRoleSummary } from '../../services/PlayerRoleService';
import { getPlayerAreaProfile } from '../../services/PlayerAreaService';
import { RoleSummaryDto, roleDisplayNames } from '../../models/PlayerRole';
import { PlayerAreaProfileDto } from '../../models/PlayerArea';
import { PlayerStats } from '../../models/PlayerStats';
import PlayerImage from '../../components/PlayerImage';
import Loading from '../../components/Loading';
import styles from '../../styles/Tables.module.css';

const T_SIDE_COLOR = '#de9b35';
const CT_SIDE_COLOR = '#5d79ae';

interface TeamRolesProps {
    players: PlayerStats[];
    season: number;
}

interface PlayerRoleData {
    steamId: string;
    name: string;
    mapRoles: RoleSummaryDto[];
    fullSummary: RoleSummaryDto[];
    areaProfile: PlayerAreaProfileDto | null;
}

const TeamRoles: React.FC<TeamRolesProps> = ({ players, season }) => {
    const [playerRoles, setPlayerRoles] = useState<PlayerRoleData[]>([]);
    const [expandedMap, setExpandedMap] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchAllRoles = async () => {
            if (!players || players.length === 0) {
                setPlayerRoles([]);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setExpandedMap(null);

            try {
                const activePlayers = players.filter(p => p.gamesPlayed > 0);
                const results = await Promise.all(
                    activePlayers.map(async (p) => {
                        const [mapRoles, fullSummary, areaProfile] = await Promise.all([
                            getPlayerMapRoles(p.steamID, season),
                            getPlayerRoleSummary(p.steamID, season),
                            getPlayerAreaProfile(p.steamID, season),
                        ]);
                        return {
                            steamId: p.steamID,
                            name: p.account?.playerName || p.steamID,
                            mapRoles,
                            fullSummary,
                            areaProfile,
                        };
                    })
                );
                setPlayerRoles(results);
            } catch (err) {
                console.error('Error fetching team roles:', err);
                setPlayerRoles([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllRoles();
    }, [players, season]);

    const formatMapName = (mapName: string): string => {
        const cleanName = mapName.replace('de_', '');
        return cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
    };

    const toggleExpand = (mapName: string) => {
        setExpandedMap(prev => prev === mapName ? null : mapName);
    };

    // Get top position (area name) for a player on a map+side
    const getPosition = (pr: PlayerRoleData, mapName: string, side: string): string | null => {
        const mapStat = pr.areaProfile?.mapStats?.find(ms => ms.mapName === mapName);
        if (!mapStat) return null;
        const topAreas = side === 'T' ? mapStat.topTSideAreas : mapStat.topCTSideAreas;
        return topAreas?.[0]?.areaName || null;
    };

    // Collect all unique maps across all players (from roles + area data)
    const allMaps = Array.from(
        new Set([
            ...playerRoles.flatMap(pr => pr.mapRoles.map(r => r.map)),
            ...playerRoles.flatMap(pr => pr.areaProfile?.mapStats?.map(ms => ms.mapName) || []),
        ])
    ).sort();

    // Compute AWP overrides: for each map+side, if no player has 'awp' as dominant role,
    // find the player with the most 'awp' rounds and promote them
    const awpOverrides = React.useMemo(() => {
        const overrides = new Map<string, string>(); // key: "mapName|side" -> steamId to promote
        if (playerRoles.length === 0) return overrides;

        for (const mapName of allMaps) {
            for (const side of ['T', 'CT']) {
                // Check if anyone already has 'awp' as dominant role for this map+side
                const hasAwpDominant = playerRoles.some(pr =>
                    pr.mapRoles.find(r => r.map === mapName && r.side === side)?.roleCategory === 'awp'
                );
                if (hasAwpDominant) continue;

                // Find the player with the most 'awp' rounds for this map+side
                let bestSteamId = '';
                let bestAwpRounds = 0;
                for (const pr of playerRoles) {
                    const awpRounds = pr.fullSummary
                        .filter(r => r.map === mapName && r.side === side && r.roleCategory === 'awp')
                        .reduce((sum, r) => sum + r.rounds, 0);
                    if (awpRounds > bestAwpRounds) {
                        bestAwpRounds = awpRounds;
                        bestSteamId = pr.steamId;
                    }
                }
                if (bestSteamId && bestAwpRounds > 0) {
                    overrides.set(`${mapName}|${side}`, bestSteamId);
                }
            }
        }
        return overrides;
    }, [playerRoles, allMaps]);

    // Get a player's dominant role for a map+side (with AWP override)
    const getRole = (pr: PlayerRoleData, mapName: string, side: string): RoleSummaryDto | undefined => {
        const dominant = pr.mapRoles.find(r => r.map === mapName && r.side === side);
        // Apply AWP override: if this player is the designated AWPer for this map+side
        const overrideSteamId = awpOverrides.get(`${mapName}|${side}`);
        if (overrideSteamId === pr.steamId && dominant && dominant.roleCategory !== 'awp') {
            return { ...dominant, roleCategory: 'awp' };
        }
        return dominant;
    };

    // Get all roles for a player on a map+side (for expanded detail)
    const getDetailRoles = (pr: PlayerRoleData, mapName: string, side: string): RoleSummaryDto[] => {
        return pr.fullSummary
            .filter(r => r.map === mapName && r.side === side)
            .sort((a, b) => b.rounds - a.rounds);
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <Loading />
            </div>
        );
    }

    if (allMaps.length === 0) {
        return (
            <div className="text-center py-5" style={{ color: 'var(--text-secondary)' }}>
                <h4>Ingen rolledata tilgængelig</h4>
                <p>Der er ingen rolledata for dette hold i denne sæson.</p>
            </div>
        );
    }

    return (
        <div className={styles.tableContainer}>

            <table className={styles.table}>
                <thead>
                    <tr className={styles.tableHeader}>
                        <th className={styles.tableHeaderCell} style={{ textAlign: 'left', minWidth: '100px' }}>
                            Map
                        </th>
                        {playerRoles.map(pr => (
                            <th key={pr.steamId} className={`${styles.tableHeaderCell} ${styles.center}`} style={{ minWidth: '120px' }}>
                                <Link to={`/player/${pr.steamId}`} className="text-decoration-none">
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                        <PlayerImage id={pr.steamId} width={32} />
                                        <span style={{ color: '#fff', fontSize: '0.8rem', fontWeight: 500 }}>
                                            {pr.name}
                                        </span>
                                    </div>
                                </Link>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {allMaps.map(mapName => {
                        const isExpanded = expandedMap === mapName;

                        return (
                            <React.Fragment key={mapName}>
                                <tr
                                    className={`${styles.tableRow} ${styles.standard}`}
                                    onClick={() => toggleExpand(mapName)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td className={styles.tableCell} style={{ textAlign: 'left', fontWeight: 500 }}>
                                        <span style={{ marginRight: '6px', fontSize: '0.75rem', opacity: 0.5 }}>
                                            {isExpanded ? '▼' : '▶'}
                                        </span>
                                        {formatMapName(mapName)}
                                    </td>
                                    {playerRoles.map(pr => {
                                        const ctRole = getRole(pr, mapName, 'CT');
                                        const tRole = getRole(pr, mapName, 'T');
                                        const ctPos = getPosition(pr, mapName, 'CT');
                                        const tPos = getPosition(pr, mapName, 'T');

                                        return (
                                            <td key={pr.steamId} className={`${styles.tableCell} ${styles.center}`}>
                                                {ctRole || tRole ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', lineHeight: 1.3 }}>
                                                        <div>
                                                            <span style={{ color: CT_SIDE_COLOR, fontWeight: 600, fontSize: '0.8rem' }}>
                                                                {ctRole ? (roleDisplayNames[ctRole.roleCategory] || ctRole.roleCategory) : '—'}
                                                            </span>
                                                            {ctPos && (
                                                                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', marginLeft: '4px' }}>
                                                                    {ctPos}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <span style={{ color: T_SIDE_COLOR, fontWeight: 500, fontSize: '0.75rem' }}>
                                                                {tRole ? (roleDisplayNames[tRole.roleCategory] || tRole.roleCategory) : '—'}
                                                            </span>
                                                            {tPos && (
                                                                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', marginLeft: '4px' }}>
                                                                    {tPos}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span style={{ opacity: 0.3 }}>—</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>

                                {/* Expanded detail row — one cell per player, aligned with columns */}
                                {isExpanded && (
                                    <tr style={{ background: 'rgba(255,255,255,0.02)' }}>
                                        <td style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: '12px 8px', verticalAlign: 'top' }}>
                                        </td>
                                        {playerRoles.map(pr => {
                                            const tRoles = getDetailRoles(pr, mapName, 'T');
                                            const ctRoles = getDetailRoles(pr, mapName, 'CT');
                                            const totalTRounds = tRoles.reduce((sum, r) => sum + r.rounds, 0);
                                            const totalCTRounds = ctRoles.reduce((sum, r) => sum + r.rounds, 0);

                                            return (
                                                <td key={pr.steamId} style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: '12px 8px', verticalAlign: 'top' }}>
                                                    {totalCTRounds === 0 && totalTRounds === 0 ? (
                                                        <div style={{ opacity: 0.3, textAlign: 'center', fontSize: '0.8rem' }}>—</div>
                                                    ) : (
                                                        <>
                                                            {totalCTRounds > 0 && (
                                                                <div style={{ marginBottom: '8px' }}>
                                                                    <div style={{ color: CT_SIDE_COLOR, fontWeight: 600, fontSize: '0.7rem', marginBottom: '4px' }}>
                                                                        CT ({totalCTRounds}r)
                                                                    </div>
                                                                    {ctRoles.map(role => (
                                                                        <RoleBar
                                                                            key={`${role.roleCategory}-${role.roleName}`}
                                                                            role={role}
                                                                            totalRounds={totalCTRounds}
                                                                            color={CT_SIDE_COLOR}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {totalTRounds > 0 && (
                                                                <div>
                                                                    <div style={{ color: T_SIDE_COLOR, fontWeight: 600, fontSize: '0.7rem', marginBottom: '4px' }}>
                                                                        T ({totalTRounds}r)
                                                                    </div>
                                                                    {tRoles.map(role => (
                                                                        <RoleBar
                                                                            key={`${role.roleCategory}-${role.roleName}`}
                                                                            role={role}
                                                                            totalRounds={totalTRounds}
                                                                            color={T_SIDE_COLOR}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

// Single role bar with percentage (same pattern as PlayerPositioner)
interface RoleBarProps {
    role: RoleSummaryDto;
    totalRounds: number;
    color: string;
}

const RoleBar: React.FC<RoleBarProps> = ({ role, totalRounds, color }) => {
    const pct = totalRounds > 0 ? (role.rounds / totalRounds) * 100 : 0;

    return (
        <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', marginBottom: '1px' }}>
                <span style={{ color: 'var(--text-primary, #fff)' }}>
                    {roleDisplayNames[role.roleCategory] || role.roleCategory}
                    <span style={{ opacity: 0.5, marginLeft: '4px', fontSize: '0.65rem' }}>{role.roleName}</span>
                </span>
                <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {role.rounds}r ({pct.toFixed(0)}%)
                </span>
            </div>
            <div style={{
                height: '3px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '2px',
                overflow: 'hidden',
            }}>
                <div style={{
                    height: '100%',
                    width: `${pct}%`,
                    background: color,
                    borderRadius: '2px',
                    transition: 'width 0.3s ease',
                }} />
            </div>
        </div>
    );
};

export default TeamRoles;
